/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import {
  AccessListService,
  AccessListUserType,
  UserStatusType,
} from '../../services/access-list.service';
import { AccessListRole, UserService } from '../../services/user.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { NgForm } from '@angular/forms';
import { InviteUserComponent } from './invite-user.component';
import { EditRoleComponent } from './edit-role.component';
import { BasicAlertComponent } from '../alerts/basic-alert.component';
import { Subscription } from 'rxjs/internal/Subscription';
import { AccountService } from '../../services/account.service';
import { Workflow, WorkflowService } from '../../services/workflow.service';
import { XdaStringsService } from '../../services/xda-strings.service';
import { XdaTranslate } from '../../pipes/xda-translate.pipe';
import { ActivityIndicatorModalComponent } from '../../components/modals/activity-indicator-modal.component';
import { ErrorService } from '../../services/error.service';
import { TableFilterPipe } from '../../pipes/table-filter.pipe';
import { pageSizeType } from '../controls/xrx-pagination.component';
import { SortParamsType } from '../../directives/sort-column.directive';

@Component({
  selector: 'access-list',
  templateUrl: './access-list.component.html',
  styleUrls: ['./access-list.component.less'],
  standalone: false,
})
export class AccessListComponent implements OnInit, OnDestroy {
  @ViewChild('f', { static: false }) form: NgForm;

  accessListUsers: AccessListUserType[];
  workflowsToOrphan: Workflow[] = [];
  refreshSub: Subscription;
  searchTerm = '';
  selectAll = false;
  isPartial = false;
  selectAllLabel = '';
  direction = 'asc';
  column = '';
  type = 'string';
  searchFilterColumns: string[] = [
    'userFirstName',
    'userLastName',
    'userEmail',
  ];
  searchMinLength = 2;
  searchPlaceholder = '';
  clearTooltip = '';
  showClearSearch = false;
  searchIsValid = true;
  modalActivityRef: NgbModalRef | null;
  page = 1;
  initialPageSize = 10;
  pageSize = 10;
  pageSizeList: pageSizeType[] = [
    { size: 5, key: '', value: '' },
    { size: 10, key: '', value: '' },
    { size: 25, key: '', value: '' },
    { size: 50, key: '', value: '' },
    { size: 100, key: '', value: '' },
  ];
  displayPagination = true;
  tableFilterMetadata = { count: 0 };

  private xdaTranslatePipe: XdaTranslate;
  private tableFilterPipe: TableFilterPipe<AccessListUserType>;
  constructor(
    private accessListService: AccessListService,
    private userService: UserService,
    private modalService: NgbModal,
    private accountService: AccountService,
    private workflowService: WorkflowService,
    private xdaStringsService: XdaStringsService,
    private errorService: ErrorService,
  ) {
    this.xdaTranslatePipe = new XdaTranslate(this.xdaStringsService);
    this.tableFilterPipe = new TableFilterPipe<AccessListUserType>();
    this.searchPlaceholder = this.xdaStringsService.strings['SDE_SEARCH'];
    this.clearTooltip = this.xdaStringsService.strings['SDE_CLEAR_SEARCH'];
  }

  ngOnInit(): void {
    if (this.userService.user?.accessListId) {
      this.getAccessListUsers();
      this.getSharedWorkflowsForAccessList();
    }
    this.refreshSub = this.accessListService.refreshAccessList.subscribe(() =>
      this.getAccessListUsers(),
    );
    this.modalActivityRef = this.modalService.open(
      ActivityIndicatorModalComponent,
      {
        windowClass: 'xrx-modal transparent',
        centered: true,
        backdrop: 'static',
      },
    );
  }

  async getAccessListUsers() {
    try {
      const json = await this.accessListService.getAccessListUsers(
        this.userService.user?.accessListId || '',
      );

      if (json === undefined) return;

      // reset select all checkbox
      this.selectAll = false;
      this.isPartial = false;

      this.accessListUsers = JSON.parse(json.toString());
      this.accessListUsers = this.accessListUsers.filter(
        (u) =>
          u.userId !== this.userService.user?.id &&
          u.accessListRole !== 'Owner',
      );
      if (this.accessListUsers.length <= this.pageSize) {
        this.displayPagination = false;
      } else {
        this.displayPagination = true;
      }

      // select all label
      this.selectAllLabel = this.xdaTranslatePipe.transform(
        'SDE_SELECTED_FMTSTR_FMTSTR',
        ['0', String(this.accessListUsers.length)],
      );

      this.accessListUsers.forEach((u) => {
        u.checked = false;
        u.userStatus.sdekey = this.mapUserStatus(u.userStatus);
        // insert translated strings for userStatus and accessListRole into accessListUsers to be used to sort the table user table correctly in different locals
        u.userStatusTranslated = this.xdaTranslatePipe.transform(
          u.userStatus.sdekey,
        );
        u.accessListRoleTranslated = this.xdaTranslatePipe.transform(
          this.mapUserRole(u.accessListRole),
        );
      });
      this.accessListService.emitRefreshSuccessEvent();
      this.modalActivityRef?.close();
    } catch (error) {
      console.log(error);
      this.errorService.displayGeneralError().result.then(() => {
        this.modalActivityRef?.close();
      });
    }
  }

  getSharedWorkflowsForAccessList() {
    this.workflowService.getSharedWorkflows()?.subscribe(
      (resp) => {
        this.workflowService.workflows = resp;
      },
      () => {
        const modalRef = this.modalService.open(BasicAlertComponent);
        modalRef.componentInstance.title = 'SDE_ERROR_GETTING_WORKFLOWS';
      },
    );
  }

  // toggle all visible jobs
  toggleAll(value: boolean): void {
    this.selectAll = value;
    this.isPartial = false;

    if (this.searchTerm.length >= this.searchMinLength) {
      // get list of users found in search
      const searchResults = this.tableFilterPipe.transform(
        this.accessListUsers,
        this.searchTerm,
        this.searchFilterColumns,
        this.searchMinLength,
      );

      // check or uncheck all search results
      this.accessListUsers.forEach((u: AccessListUserType) => {
        if (searchResults.includes(u)) {
          u.checked = value;
        }
      });
    } else {
      // check or uncheck all users
      this.accessListUsers.forEach((u) => {
        u.checked = value;
      });
    }
    this.setSelectAllLabel();
  }

  // check to see any of the visible users are selected or not
  anyUsersSelected(): void {
    const numberOfUsersSelected = this.accessListUsers.filter(
      (u) => u.checked == true,
    );
    let selectAll = false,
      isPartial = false;

    // set the value of the selectAll checkbox
    if (this.searchTerm.length >= this.searchMinLength) {
      // get list of users found in search
      const searchResults = this.tableFilterPipe.transform(
        this.accessListUsers,
        this.searchTerm,
        this.searchFilterColumns,
        this.searchMinLength,
      );
      selectAll = searchResults.every((u: AccessListUserType) =>
        numberOfUsersSelected.includes(u),
      );
      isPartial =
        searchResults.some((u: AccessListUserType) =>
          numberOfUsersSelected.includes(u),
        ) && !selectAll;
    } else {
      selectAll =
        numberOfUsersSelected.length == this.accessListUsers.length
          ? true
          : false;
      isPartial =
        numberOfUsersSelected.length < this.accessListUsers.length &&
        numberOfUsersSelected.length > 0
          ? true
          : false;
    }

    this.selectAll = selectAll;
    this.isPartial = isPartial;
  }

  rowClicked(user: AccessListUserType): void {
    user.checked = !user.checked;
    this.anyUsersSelected();
    this.setSelectAllLabel();
  }

  onChecked(): void {
    this.anyUsersSelected();
    this.setSelectAllLabel();
  }

  setSelectAllLabel(): void {
    this.selectAllLabel = this.xdaTranslatePipe.transform(
      'SDE_SELECTED_FMTSTR_FMTSTR',
      [
        this.accessListUsers.filter((u) => u.checked == true).length.toString(),
        String(this.accessListUsers.length),
      ],
    );
  }

  setSortParams(param: SortParamsType): void {
    this.direction = param.direction;
    this.column = param.column;
    this.type = param.type;
  }

  mapUserStatus(status: UserStatusType): string {
    switch (status.value) {
      case 'Suspended':
      case 'Locked':
        return 'SDE_INACTIVE';
      case 'DeleteRequested':
        return 'SDE_ACTIVE';
      case 'RejectedBySystem':
      case 'RejectedByAdmin':
        return 'SDE_REJECTED';
      default:
        return status.sdekey;
    }
  }

  mapUserRole(accessListRole: AccessListRole): string {
    switch (accessListRole) {
      case 'User':
        return 'SDE_USER';
      case 'Manager':
        return 'SDE_MANAGER';
      default:
        return accessListRole;
    }
  }

  isInviteEnabled(): boolean {
    let result = true;
    // Invite is disabled for users with an authType (enterprise accounts)
    if (this.userService.user?.authType) {
      result = false;
      // Unless they are the owner of said account
      if (this.userService.user?.accessListRole === AccessListRole.Owner) {
        result = true;
      }
    }
    // Invite is disabled if subscription is expired
    if (this.isSubscriptionExpired()) {
      result = false;
    }
    return result;
  }

  isSubscriptionExpired(): boolean {
    let result = false;
    if (this.userService && this.userService.user?.isSubscriptionExpired) {
      result = true;
    }
    return result;
  }

  inviteUser() {
    // If user has an authType (enterprise accounts) and they are the owner
    // redirect them to AG to invite users
    const userRole = this.userService.user?.accessListRole;
    if (this.userService.user?.authType && userRole === AccessListRole.Owner) {
      const url = this.xdaStringsService.strings['AGTRYITURL'];
      return (window.location.href = url);
    }
    this.modalService.open(InviteUserComponent, { windowClass: 'xrx-modal' });
    return;
  }

  displayResendAlert(userId: string, email: string) {
    const modalRef = this.modalService.open(BasicAlertComponent);
    modalRef.componentInstance.title = 'SDE_CONFIRM_RESEND_USERS';
    modalRef.componentInstance.body = email;
    modalRef.componentInstance.buttonText = 'SDE_CANCEL';
    modalRef.componentInstance.okButtonText = 'SDE_RESEND';
    modalRef.result.then((data) => {
      if (data === 'Ok') {
        this.resendInvite(userId);
      }
    });
  }

  async resendInvite(userId: string) {
    try {
      await this.accountService.resendInvite(userId);
    } catch (error) {
      console.log(error);
      this.errorService.displayGeneralError();
    }
  }

  canEnable() {
    return this.accessListUsers && this.accessListUsers.some((x) => x.checked);
  }

  editRole() {
    const modalRef = this.modalService.open(EditRoleComponent, {
      windowClass: 'xrx-modal modal-sm',
    });
    modalRef.componentInstance.accessListUsers = this.accessListUsers.filter(
      (u) => u.checked,
    );
  }

  displayAlert() {
    const modalRef = this.modalService.open(BasicAlertComponent);
    modalRef.componentInstance.title = this.xdaTranslatePipe.transform(
      'SDE_CONFIRM_REMOVE_FMTSTR41',
      [this.accessListUsers.filter((u) => u.checked == true).length.toString()],
    );
    modalRef.componentInstance.buttonText = 'SDE_CANCEL';
    modalRef.componentInstance.dangerButtonText = 'SDE_REMOVE';
    modalRef.result.then((data) => {
      if (data === 'Delete') {
        this.removeFromAccessList();
      }
    });
  }

  async removeFromAccessList() {
    Promise.all(
      this.accessListUsers.map(async (user) => {
        try {
          if (user.checked) {
            await this.accessListService.removeFromAccessList(user);
            this.orphanWorkflowsForRemovedUser(user);
          }
        } catch (error) {
          console.log(`error removing user: ${user.userId}`);
          console.log(error);
          this.errorService.displayGeneralError();
        }
      }),
    ).then(() => {
      this.accessListService.emitRefreshEvent();
    });
  }

  orphanWorkflowsForRemovedUser(user: AccessListUserType) {
    this.workflowsToOrphan = this.workflowService.workflows.filter(
      (x) => x.creator == user.userId,
    );
    this.workflowsToOrphan.forEach((workflow) => {
      this.workflowService.setWorkflowOrphanFlag(workflow, true).subscribe(
        () => {
          // do nothing
        },
        (error) => {
          console.log(error);
        },
      );
    });
  }

  validateSearch(event: KeyboardEvent | FocusEvent) {
    // both keyup and blur call this function so figure out which event triggered the validation check
    if (event.type == 'keyup') {
      const keyboardEvent = event as KeyboardEvent;
      // for keyup we only want to validate the searchTerm length is less then the min length but greater than 0 if the enter key is hit
      if (
        keyboardEvent.keyCode == 13 &&
        this.searchTerm.length < this.searchMinLength &&
        this.searchTerm.length > 0
      ) {
        this.searchIsValid = false;
      } else {
        this.searchIsValid = true;
      }
    } else {
      // on blur check if the searchTerm length is less then the min length but greater than 0
      if (
        this.searchTerm.length < this.searchMinLength &&
        this.searchTerm.length > 0
      ) {
        this.searchIsValid = false;
      } else {
        this.searchIsValid = true;
      }
    }

    // show the clear search button if the searchTerm is greater then or equal to 1
    this.showClearSearch = this.searchTerm.length >= 1 ? true : false;

    // reset selected on search
    this.anyUsersSelected();
    this.setSelectAllLabel();
  }

  clearSearch() {
    this.searchTerm = '';
    this.searchIsValid = true;
    this.showClearSearch = false;
    this.anyUsersSelected();
  }

  ngOnDestroy() {
    this.refreshSub.unsubscribe();
  }
}
